import React, { useState, useEffect, useMemo } from "react";
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
  Portal
} from "@headlessui/react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import type { State } from "../../../shared/types/models/location";
import { useKeycloak } from "../config/KeycloakContext";
import { useUserContext } from "../config/UserContext";

interface StateSelectorProps {
  value: State | null;
  onChange: (value: State | null) => void;
  label?: string;
  disabled?: boolean;
  className?: string;
  inputTestId?: string;
  endpoint?: string;
  /**
   * Optional filter object to be serialized as query parameters for the API call.
   * Example: { enabled: true } will result in /api/frozendeed/states?enabled=true
   * If not provided, all states will be fetched.
   */
  filter?: Record<string, string | number | boolean>;
  /**
   * Controls the dropdown anchor position. If unset, uses Headless UI's default ('bottom').
   * Accepts 'top', 'bottom', 'left', 'right', etc. or an anchor object (see Headless UI docs)
   */
  dropdownAnchor?: string | { to: string, gap?: number | string, offset?: number | string, padding?: number | string };
}

interface AppConfig {
  API_BASE_URL: string;
}

const StateSelector: React.FC<StateSelectorProps> = ({
  value,
  onChange,
  label = "State",
  disabled = false,
  className = "",
  inputTestId = "state-selector",
  endpoint,
  filter,
  dropdownAnchor
}) => {
  const [query, setQuery] = useState("");
  const [states, setStates] = useState<State[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { getToken } = useKeycloak();
  const { apiBaseUrl, apiBaseUrlLoading, apiBaseUrlError } = useUserContext();

  useEffect(() => {
    let isMounted = true;
    if (apiBaseUrlLoading) {
      setLoading(true);
      return () => { isMounted = false; };
    }
    if (apiBaseUrlError) {
      setError(apiBaseUrlError);
      setLoading(false);
      return () => { isMounted = false; };
    }
    if (!apiBaseUrl) {
      setError("API base URL not set");
      setLoading(false);
      return () => { isMounted = false; };
    }
    setLoading(true);
    setError(null);
    getToken().then(token => {
      if (!token) {
        setError("Not authenticated");
        setLoading(false);
        return;
      }
      let statesEndpoint = endpoint || "/api/frozendeed/states";
      if (!endpoint && filter && Object.keys(filter).length > 0) {
        const params = new URLSearchParams();
        Object.entries(filter).forEach(([key, value]) => {
          params.append(key, String(value));
        });
        statesEndpoint += `?${params.toString()}`;
      }
      fetch(`${apiBaseUrl}${statesEndpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(res => {
          if (!res.ok) throw new Error("Failed to fetch states");
          return res.json();
        })
        .then((data) => {
          if (isMounted) {
            setStates(data.data || []);
            setLoading(false);
          }
        })
        .catch((err) => {
          if (isMounted) {
            setError(err.message);
            setLoading(false);
          }
        });
    });
    return () => { isMounted = false; };
  }, [getToken, apiBaseUrl, apiBaseUrlLoading, apiBaseUrlError, endpoint, filter]);

  const filteredStates = useMemo(() => {
    if (!query) return states;
    return states.filter(state =>
      state.name.toLowerCase().includes(query.toLowerCase()) ||
      state.abbr.toLowerCase().includes(query.toLowerCase())
    );
  }, [query, states]);

  const selectedState = value;

  // Determine placeholder and disabled state
  let inputPlaceholder = "Select State";
  if (apiBaseUrlLoading || loading) inputPlaceholder = "Loading states...";
  else if (error) inputPlaceholder = `Error: ${error}`;
  else if (apiBaseUrlError) inputPlaceholder = `Error: ${apiBaseUrlError}`;

  const isInputDisabled = disabled || loading || !!error;

  return (
    <div className={clsx("w-full relative", className)}>
      {label && (
        <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta mb-1 block">
          {label}
        </label>
      )}
      <Combobox value={selectedState} onChange={onChange} disabled={isInputDisabled} by={(a, b) => a?.id === b?.id}>
        <div className="relative">
          <ComboboxInput
            className={clsx(
              "w-full rounded-lg border border-[#D7DFEC] bg-white py-2 pr-8 pl-3 text-sm text-[#141435] font-plus-jakarta font-semibold",
              "focus:outline-none focus:ring-2 focus:ring-blue-200",
              isInputDisabled && "bg-gray-100 cursor-not-allowed"
            )}
            displayValue={(state: State | null) => (state ? state.name : "")}
            onChange={e => setQuery(e.target.value)}
            disabled={isInputDisabled}
            placeholder={inputPlaceholder}
            data-testid={inputTestId}
            data-state-key={selectedState ? selectedState.abbr : ''}
          />
          <ComboboxButton className="group absolute inset-y-0 right-0 flex items-center px-2.5">
            <ChevronDownIcon className="size-4 text-[#485D7D] group-data-hover:text-blue-700" />
          </ComboboxButton>
        </div>
        <ComboboxOptions
          anchor={dropdownAnchor as any}
          className={clsx(
            "w-[--input-width] rounded-xl border border-[#D7DFEC] bg-white p-1 mt-1 shadow-lg z-50",
            "empty:invisible"
          )}
        >
          {loading && (
            <div className="relative cursor-default select-none px-4 py-2 text-gray-400">
              Loading states...
            </div>
          )}
          {error && !loading && (
            <div className="relative cursor-default select-none px-4 py-2 text-red-500">
              Error: {error}
            </div>
          )}
          {!loading && !error && filteredStates.length === 0 && (
            <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
              No states found
            </div>
          )}
          {filteredStates.map(state => (
            <ComboboxOption
              key={state.id}
              value={state}
              className={({ active, selected }) =>
                clsx(
                  "group flex cursor-default items-center gap-2 rounded-lg px-3 py-1.5 select-none",
                  active ? "bg-blue-100 text-blue-900" : "text-[#141435]",
                  selected && "font-semibold"
                )
              }
            >
              <CheckIcon className={clsx("size-4 text-blue-600", "invisible group-data-selected:visible")}/>
              <div className="text-sm font-plus-jakarta">{state.name}</div>
            </ComboboxOption>
          ))}
        </ComboboxOptions>
      </Combobox>
    </div>
  );
};

export default StateSelector; 